import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type InvoiceParsingInputParams,
  type InvoiceParsingInputResponse,
} from './types';
import { zodInvoiceParsingInputEndpointSchema } from './validations';

/**
 * Fetches the parsing input of an invoice process based on the process ID.
 *
 * @param params - The parameters for fetching the invoice parsing input.
 * @returns The invoice parsing input data or undefined if not found.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_parsing_input_invoice_process__process_id__parsing_input_get
 */
export const fetchInvoiceParsingInput = async (
  params: InvoiceParsingInputParams,
): Promise<InvoiceParsingInputResponse | undefined> => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_REPROCESSING.GET_PARSING_INPUT(params.processId), {
        validate: { pathSegments: zodInvoiceParsingInputEndpointSchema },
      })
      .json<InvoiceParsingInputResponse>();

    return response;
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 404) {
      // 404 error is expected if the invoice has no parsing input
      return undefined;
    }

    log.error({
      message: 'Error fetching invoice parsing input',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      errorMessage = i18n.t(($) => $.fetchInvoiceParsingInput.error.generic, {
        ns: 'data',
      });
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
